import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Button, Modal, Input, Select, Textarea, Spinner, message } from '../../ui';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';
import { FileUploadField } from '../../components/FileUploadField';
import { bankAcctOption } from '../billing/PaymentFormModal';
import { PayExpenseReportModal } from './PayExpenseReportModal';
import { errorText } from './SettlementApproveDialog';
import { WalletTopUpDialog } from './WalletTopUpDialog';

const PAY_MODES = ['BANK_TRANSFER', 'UPI', 'CHEQUE', 'CASH', 'CARD', 'OTHER'];

/**
 * PAY A REIMBURSEMENT — the step after an approval that left the company owing the rep.
 *
 * Everything about HOW MUCH was fixed at approval (wallet used, collection used, reimbursement), so this
 * dialog only records the payment: how, from which company account, the reference and the proof. The full
 * frozen amount is paid, once, and the rep's wallet does not change — this was money owed to them, not new
 * working float. Claims approved under the old rule still open the old dialog.
 */
export function PayReimbursementDialog({ reportId, onClose, onDone }: { reportId: string; onClose: () => void; onDone?: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<string | undefined>();
  const [bank, setBank] = useState<string | undefined>();
  const [reference, setReference] = useState('');
  const [proof, setProof] = useState<string | undefined>();
  const [remarks, setRemarks] = useState('');
  // New float for the rep is a separate payment — offered here, never mixed into the reimbursement.
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    refetchOnMount: 'always',
  });
  const { data: banks } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });

  const s = report?.settlementV2 ?? null;
  const rb = s?.reimbursement ?? null;
  const owed = rb?.status === 'OUTSTANDING' ? Number(rb.amount) : 0;
  const repName = report?.repName ?? 'the rep';

  const pay = useConfirmedMutation(
    { title: `Pay ${inr2(owed)} to ${repName}?`, content: 'The reimbursement is recorded as paid and the claim closes. The wallet does not change.', okText: 'Yes, pay' },
    {
      mutationFn: () => request({
        url: `/expense-reports/${reportId}/close`, method: 'PATCH',
        data: {
          paymentMode: mode, ...(mode !== 'CASH' ? { bankAccountId: bank } : {}),
          ...(reference.trim() ? { paymentReference: reference.trim() } : {}),
          paymentProofUrl: proof, ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        },
      }),
      onSuccess: () => {
        message.success(`${inr2(owed)} reimbursement for ${report?.reportNo} recorded as paid`);
        qc.invalidateQueries({ queryKey: ['expense-report', reportId] });
        qc.invalidateQueries({ queryKey: ['expense-reports'] });
        qc.invalidateQueries({ queryKey: ['approvals'] });
        qc.invalidateQueries({ queryKey: ['badges'] });
        onDone?.();
        onClose();
      },
      onError: (e: any) => message.error(errorText(e)),
    },
  );

  // A claim settled under the old rule keeps its old payout dialog.
  if (report && !s) return <PayExpenseReportModal reportId={reportId} onClose={onClose} />;

  const missing = !mode ? 'Choose how it was paid.'
    : mode !== 'CASH' && !bank ? 'Choose the company account it was paid from.'
      : !proof ? 'Attach the payment proof.' : null;

  return (
    <Modal
      open
      width={640}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={report ? `Pay reimbursement · ${report.reportNo}` : 'Pay reimbursement'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={pay.isPending} disabled={!owed || !!missing || report?.status !== 'APPROVED'} onClick={() => pay.mutate()}>
            {owed ? `Pay ${inr2(owed)}` : 'Pay'}
          </Button>
        </>
      }
    >
      {isLoading || !report ? (
        isError ? <div className="text-sm text-danger-600 dark:text-danger-500">Couldn’t load this claim.</div>
          : <div className="flex items-center justify-center gap-2 py-8 text-muted"><Spinner /> Loading…</div>
      ) : !owed ? (
        <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
          {report.reportNo} has no reimbursement waiting — {rb?.status === 'PAID' ? `it was paid${rb.paidAt ? '' : ''}.` : 'nothing is owed on it.'}
        </div>
      ) : (
        <div className="space-y-4">
          <section className="rounded-card border border-line bg-surface px-3 py-2.5 text-sm">
            <h4 className="mb-1.5 type-overline text-subtle">Settlement — fixed at approval{s?.approvedByName ? ` by ${s.approvedByName}` : ''}</h4>
            <dl className="space-y-1 tabular-nums">
              <div className="flex justify-between gap-3"><dt className="text-muted">Approved expense</dt><dd className="text-content">{inr2(s.approved)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted">From wallet</dt><dd className="text-content">{inr2(s.walletUsed)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted">From collection</dt><dd className="text-content">{inr2(s.collectionUsed)}</dd></div>
              <div className="flex justify-between gap-3 border-t border-line pt-1">
                <dt className="font-medium text-content">Reimbursement to pay {repName}</dt>
                <dd className="font-semibold text-warning-600 dark:text-harvest-300">{inr2(owed)}</dd>
              </div>
            </dl>
            <p className="mt-1.5 type-caption text-muted">
              Paid in full. {repName}’s wallet does not change.{' '}
              <button type="button" onClick={() => setTopUpOpen(true)}
                className="font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-400">
                Give them new float as well
              </button>
            </p>
            {topUpOpen && <WalletTopUpDialog reportId={reportId} onClose={() => setTopUpOpen(false)} />}
          </section>

          <div>
            <label className="mb-1 block type-label text-content">Paid via <span className="text-danger-600">*</span></label>
            <Select className="w-full" placeholder="How was it paid?" value={mode} onChange={setMode}
              options={PAY_MODES.map((m) => ({ value: m, label: m.replace(/_/g, ' ') }))} />
          </div>
          {mode && mode !== 'CASH' && (
            <div>
              <label className="mb-1 block type-label text-content">From account <span className="text-danger-600">*</span></label>
              <Select className="w-full" placeholder="Company bank account" value={bank} onChange={setBank}
                options={(banks ?? []).map(bankAcctOption)}
                notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup" />
            </div>
          )}
          <div>
            <label className="mb-1 block type-label text-content">Reference <span className="text-subtle">(UTR / cheque / voucher no.)</span></label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. UTR 526114839027" />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">Payment proof <span className="text-danger-600">*</span></label>
            <FileUploadField category="receipt" value={proof} onChange={(url) => setProof(url)}
              buttonLabel={proof ? 'Replace proof' : 'Upload screenshot / slip / signed voucher'} />
          </div>
          <div>
            <label className="mb-1 block type-label text-content">Remarks <span className="text-subtle">(optional)</span></label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          {missing && <p className="type-caption text-subtle">{missing}</p>}
        </div>
      )}
    </Modal>
  );
}
